import { Account, type Club, ClubPlayer, Group } from "@mixitone/models";
import {
  type AssignableModelFields,
  type GetModelConfig,
  List,
  type Model,
  type ModelConstructor,
  type ModelFields,
} from "@mixitone/oom";
import { SUPPORTED_SHAPES } from "@mixitone/util";
import Color from "color";
import { indexForNewGroup } from "lib/groupIndex";
import { injectClubPlayerSessionDates, needsClubPlayerFirstSessionDate } from "lib/clubPlayerSessionDates";
import type { ResourceDefinition, ResourceListOptions } from "services/AgentToolsService";
import { resource } from "services/AgentToolsService";

function addFactory<M extends Model<any>>(
  model: ModelConstructor<M>,
  defaults: () => AssignableModelFields<GetModelConfig<M>>,
) {
  return async (data: AssignableModelFields<GetModelConfig<M>> & Record<string, any>) => {
    const attrs = {
      ...data,
      ...defaults(),
      ...(model.hasField("account_id") ? { account_id: Account.current!.id } : {}),
    } as AssignableModelFields<GetModelConfig<M>> & Record<string, any>;

    const instance = new model(attrs);
    await instance.save();
    return instance as M;
  };
}

function updateFactory<M extends Model<any>>(model: ModelConstructor<M>, list: () => Promise<List<M>>) {
  return async (id: string, data: Partial<ModelFields<GetModelConfig<M>>>) => {
    const instance = (await list()).findById(id);
    if (!instance) throw new Error(`${model.tableName} with id ${id} not found`);
    instance.writeAttributes(data);
    await instance.save();
    return instance;
  };
}

export function groupAgentFields(): NonNullable<ResourceDefinition<Group>["fields"]> {
  const fields: NonNullable<ResourceDefinition<Group>["fields"]> = {
    name: { required: true },
    color: { type: "string — rgb hex value (e.g. #2196F3)" },
    shape: { type: `string — one of: ${SUPPORTED_SHAPES.join(", ")}` },
    after: {
      type: "string — id of the group to insert after; omit to add at the start of the list",
    },
  };

  if (Account.current?.hasFeature("group_levels")) {
    fields.graduation_enabled = {
      type: "boolean — enable 1–3 graduation levels for players in this group",
    };
  }

  return fields;
}

function sanitizeGroupExtraFields(data: Record<string, unknown>) {
  const fields = { ...data };

  if (!Account.current?.hasFeature("group_levels")) {
    delete fields.graduation_enabled;
  }

  return fields;
}

/** Add a group with fractional index ordering and hex color conversion (agent / shared resource). */
async function addGroupAgent(
  groups: List<Group>,
  clubId: string,
  data: Record<string, unknown>,
): Promise<Group> {
  const { after, name, color, ...rest } = data;
  if (typeof name !== "string" || !name) {
    throw new Error("name is required");
  }
  const index = await indexForNewGroup(groups, typeof after === "string" ? after : undefined);
  const extra = sanitizeGroupExtraFields(rest);

  const group = new Group({
    name,
    color: color ? Color(color as string).rgbNumber() : undefined,
    ...extra,
    index,
    club_id: clubId,
    account_id: Account.current!.id,
  });
  await group.save();
  return group;
}

/** Update a group with hex color conversion (agent / shared resource). */
async function updateGroupAgent(
  groups: List<Group>,
  id: string,
  data: Record<string, unknown>,
): Promise<Group> {
  const group = groups.findById(id);
  if (!group) throw new Error(`group with id ${id} not found`);

  const fields = sanitizeGroupExtraFields({ ...data });
  delete fields.after;

  if (fields.color) {
    fields.color = Color(fields.color as string).rgbNumber();
  }

  group.writeAttributes(fields);
  await group.save();
  return group;
}

type SharedAgentResourcesState = {
  club: Club;
  clubPlayers?: List<ClubPlayer>;
  groups?: List<Group>;
};

export function sharedAgentResources(state: () => SharedAgentResourcesState) {
  const getClubPlayersList = async (options?: ResourceListOptions) => {
    const currentState = state();
    const clubId = currentState.club?.id;
    if (!clubId) return new List<ClubPlayer>();

    const clubPlayers =
      currentState.clubPlayers ?? new List(...(await ClubPlayer.query().eq("club_id", clubId).all()));

    if (needsClubPlayerFirstSessionDate(options)) {
      await injectClubPlayerSessionDates(clubId, clubPlayers);
    }
    return clubPlayers;
  };

  const getGroupsList = async (state: SharedAgentResourcesState) => {
    if (state.groups) return state.groups;
    if (!state.club?.id) return new List<Group>();
    const groups = await Group.query().eq("club_id", state.club.id).orderBy("index").all();
    return new List(...groups);
  };

  return {
    club_players: resource("club_players", ClubPlayer, {
      list: (options) => getClubPlayersList(options),
      fields: {
        name: { required: true },
        email: {},
        phone: {},
        active: {},
        group_id: { required: true },
        graduation_level: {},
      },
      add: addFactory(ClubPlayer, () => ({
        club_id: state().club.id,
      })),
      update: updateFactory(ClubPlayer, () => getClubPlayersList()),
    }),
    groups: resource("groups", Group, {
      list: () => getGroupsList(state()),
      fields: groupAgentFields(),
      add: async (data) => addGroupAgent(await getGroupsList(state()), state().club.id!, data),
      update: async (id, data) => updateGroupAgent(await getGroupsList(state()), id, data),
    }),
  };
}
